import { useEffect, useRef } from 'react'
import { isSessionExpired } from '~/atoms/SessionExpiredState'
import { captureException } from '~/services/sentry'

import {
  CANCEL_ERROR,
  CONNECTION_ERROR,
  NETWORK_ERROR,
  TIMEOUT_ERROR,
} from '@mangovoice/apisauce'

const GATEWAY_FAILURE_TAG = 'ccua-rest-gateway'

const TRANSIENT_PROBLEMS = new Set<string>([
  CANCEL_ERROR,
  CONNECTION_ERROR,
  NETWORK_ERROR,
  TIMEOUT_ERROR,
])

/** Consecutive no-response failures a polling query hits before it is reported. */
export const TRANSIENT_FAILURES_BEFORE_REPORT = 3

const MAX_TRANSIENT_RETRIES = 1

const reportedErrors = new WeakSet<object>()

const httpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return undefined
  }
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' ? status : undefined
}

const transportProblem = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('problem' in error)) {
    return undefined
  }
  const problem = (error as { problem?: unknown }).problem
  return typeof problem === 'string' && problem.length > 0 ? problem : undefined
}

/** The transport problem, only when the request got no HTTP status. */
const noResponseProblem = (error: unknown): string | undefined => {
  const status = httpStatus(error)
  return status == null || status === 0 ? transportProblem(error) : undefined
}

/**
 * True when the request never got an HTTP response (timeout, offline, DNS,
 * aborted). These come from the client's network, not from the gateway.
 */
export const isTransientNetworkFailure = (error: unknown): boolean => {
  const problem = transportProblem(error)
  if (problem !== undefined) return TRANSIENT_PROBLEMS.has(problem)
  return httpStatus(error) === 0
}

/** TanStack `retry` policy: retry once on a no-response failure only. */
export const retryTransientGatewayFailure = (
  failureCount: number,
  error: unknown,
): boolean =>
  isTransientNetworkFailure(error) && failureCount < MAX_TRANSIENT_RETRIES

const toException = (error: unknown, source: string): Error => {
  if (error instanceof Error) return error
  if (typeof error === 'string' && error.length > 0) return new Error(error)
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return new Error(message)
    }
  }
  const problem = noResponseProblem(error)
  return new Error(
    `Call center gateway query failed (${source})${problem ? `: ${problem}` : ''}`,
  )
}

/**
 * Sends a call-center gateway failure to Sentry.
 * Pass `ignoreQueueMembershipNotFound` for gateway status writes: HTTP 404
 * there is the expected "not in a queue" modal, not an outage.
 */
export const captureCcuaGatewayQueryFailure = (
  error: unknown,
  {
    enabled,
    pbxId,
    source,
    ignoreQueueMembershipNotFound = false,
    consecutiveFailures,
  }: {
    enabled: boolean
    pbxId?: number
    source: string
    ignoreQueueMembershipNotFound?: boolean
    consecutiveFailures?: number
  },
): void => {
  // Requests are cancelled on purpose while the session is expired.
  if (!enabled || isSessionExpired()) return
  const status = httpStatus(error)
  if (ignoreQueueMembershipNotFound && status === 404) return
  if (error !== null && typeof error === 'object') {
    if (reportedErrors.has(error)) return
    reportedErrors.add(error)
  }
  const problem = transportProblem(error)
  const noResponse = noResponseProblem(error)

  captureException(toException(error, source), {
    tags: {
      ...(pbxId != null ? { pbx_id: pbxId } : {}),
      feature_flag: GATEWAY_FAILURE_TAG,
    },
    extra: {
      source,
      ...(status != null ? { status } : {}),
      ...(problem != null ? { problem } : {}),
      ...(consecutiveFailures != null
        ? { consecutive_failures: consecutiveFailures }
        : {}),
    },
    fingerprint: [
      GATEWAY_FAILURE_TAG,
      source,
      ...(status == null ? [] : [String(status)]),
      ...(noResponse == null ? [] : [noResponse]),
    ],
  })
}

export const useReportCcuaGatewayQueryError = ({
  enabled,
  pbxId,
  source,
  error,
  isError,
  isSuccess,
  queryKey,
  transientFailuresBeforeReport = 1,
}: {
  enabled: boolean
  pbxId?: number
  source: string
  queryKey: readonly unknown[]
  /** Polling queries can wait out brief network drops before reporting. */
  transientFailuresBeforeReport?: number
  error: unknown
  isError: boolean
  isSuccess: boolean
}): void => {
  // One report per failure episode. A poll that keeps failing replaces `error`
  // every tick; wait until the query succeeds before reporting that source again.
  // A refetch with no cached data passes through `pending`, which is neither.
  // Switching PBX, source or query key starts a new episode, ignoring the error
  // still showing from before the switch.
  const reportedEpisode = useRef(false)
  const transientFailures = useRef(0)
  const lastCountedError = useRef<unknown>(undefined)
  const episodeKey = useRef<string | undefined>(undefined)
  const staleError = useRef<unknown>(undefined)

  const key = `${pbxId}:${source}:${JSON.stringify(queryKey)}`

  useEffect(() => {
    const keyChanged =
      episodeKey.current !== undefined && key !== episodeKey.current
    if (!enabled || isSuccess || key !== episodeKey.current) {
      episodeKey.current = key
      reportedEpisode.current = false
      transientFailures.current = 0
      lastCountedError.current = undefined
      staleError.current = keyChanged ? error : undefined
      if (!enabled || isSuccess) return
    }
    if (!isError || reportedEpisode.current || error === staleError.current) {
      return
    }

    const transient = isTransientNetworkFailure(error)
    if (transient) {
      if (error !== lastCountedError.current) {
        lastCountedError.current = error
        transientFailures.current += 1
      }
      if (transientFailures.current < transientFailuresBeforeReport) return
    }

    reportedEpisode.current = true
    captureCcuaGatewayQueryFailure(error, {
      enabled: true,
      pbxId,
      source,
      ...(transient ? { consecutiveFailures: transientFailures.current } : {}),
    })
  }, [
    enabled,
    error,
    isError,
    isSuccess,
    key,
    pbxId,
    source,
    transientFailuresBeforeReport,
  ])
}
